import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, setToken, pingHealth, withNetworkRetry } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { navigate("/login"); return; }
    const session_id = m[1];
    (async () => {
      try {
        // Warm the pod first in case we're coming back from a long OAuth round-trip while
        // the preview backend has gone idle. Then auto-retry the session POST once on bare
        // network errors so a cold start doesn't surface as "Network Error".
        await pingHealth();
        const r = await withNetworkRetry(() => api.post("/auth/session", { session_id }));
        // Persist token client-side too so requests work even when 3rd-party
        // cookies are blocked (Safari ITP / Chrome partitioning).
        if (r.data?.session_token) setToken(r.data.session_token);
        setUser(r.data.user);
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true, state: { user: r.data.user } });
      } catch (e) {
        // Preserve ACTUAL error details in the console (user requirement #13: real root
        // cause visible instead of only "Network Error"). The user-visible banner stays
        // short and friendly.
        // eslint-disable-next-line no-console
        console.error("[auth/session] failed:", {
          status: e.response?.status,
          data: e.response?.data,
          code: e.code,
          message: e.message,
        });
        let msg = e.response?.data?.detail || e.message || "Access denied.";
        if (msg === "Network Error" || e.code === "ERR_NETWORK" || e.code === "ECONNABORTED") {
          msg = "The server didn't respond. Please try again in a moment — if this is a newly deployed URL, your admin needs to set CORS_ORIGINS and redeploy.";
        }
        navigate("/login", { replace: true, state: { error: msg } });
      }
    })();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex items-center gap-3 text-slate-700">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Signing you in…</span>
      </div>
    </div>
  );
}
